<template>
  <v-container fluid class="pa-0">
    <!-- Header section -->
    <v-sheet color="primary" class="py-8 mb-8" rounded="0" elevation="1">
      <v-row justify="center" class="text-center mt-2">
        <v-icon icon="mdi-account-cog-outline" size="56" color="white" class="mb-4"></v-icon>
        <h1 class="text-h3 font-weight-light text-white">{{ t('personamanagement.title') }}</h1>
      </v-row>
      <v-row justify="center" class="text-center">
        <div class="text-subtitle-1 text-white mt-2">{{ t('personamanagement.description') }}</div>
      </v-row>
    </v-sheet>

    <div class="d-flex flex-wrap justify-space-between align-center mb-4 mx-8 ga-4">
      <div class="controls-left d-flex align-center ga-4">
        <v-select
            class="domain-select"
            :label="$t('generic.domain')"
            :items="domainOptions"
            item-title="title"
            item-value="value"
            v-model="selectedDomain"
            @update:modelValue="onDomainChange"
            density="default"
            variant="outlined"
            color="primary"
            prepend-inner-icon="mdi-domain"
            hide-details
        />

        <v-text-field
            class="search-field"
            v-model="searchQuery"
            :aria-label="t('personamanagement.searchPlaceholder')"
            :placeholder="t('personamanagement.searchPlaceholder')"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            color="primary"
            density="default"
            clearable
            hide-details
        />
      </div>

      <div class="d-flex flex-wrap ga-3">
        <v-btn variant="outlined" color="primary" prepend-icon="mdi-plus" @click="addDomainDialog = true">
          {{ t('personamanagement.addDomain') }}
        </v-btn>
        <v-btn variant="outlined" color="primary" prepend-icon="mdi-cog-outline" @click="goToFrameworkEditor">
          {{ t('personamanagement.editPersonaAttributes') }}
        </v-btn>
        <v-btn color="primary" prepend-icon="mdi-plus" @click="goToCreatePersona">
          {{ t('personamanagement.createPersona') }}
        </v-btn>
      </div>
    </div>

    <v-row class="mx-4">
      <v-col cols="12">
        <v-card variant="outlined" rounded="lg" class="pa-6 persona-management-card">
          <!-- Scoped to just this table (not global Vuetify config) so the
               built-in pagination footer ("Items per page" etc.) follows
               the app locale without affecting any other page. Vuetify's
               own en/ja message bundles are passed explicitly since the
               root Vuetify config only registers "en". -->
          <v-locale-provider :locale="$i18n.locale" :messages="{ en: vuetifyEn, ja: vuetifyJa }">
          <v-data-table
              :headers="headers"
              :items="filteredPersonas"
              :loading="loading"
              :loading-text="t('personamanagement.loading')"
              :no-data-text="t('personamanagement.noData')"
              item-value="persona_id"
              class="personas-table"
          >
            <template #headers="{ columns }">
              <tr class="table-header-row">
                <th
                    v-for="(col, i) in columns"
                    :key="col.key"
                    class="font-weight-bold text-black"
                    :style="i === 0 ? 'padding-left: 24px;' : (col.key === 'actions' ? 'text-align: center !important;' : '')"
                >
                  {{ col.title }}
                </th>
              </tr>
            </template>

            <template #item.name="{ item }">
              <div class="d-flex align-center ga-3 py-2 persona-name-cell">
                <v-avatar size="48" rounded="circle" class="flex-shrink-0 persona-avatar">
                  <v-img :src="item.image || genericPersonaImage" cover :alt="item.name"></v-img>
                </v-avatar>
                <div>
                  <div class="font-weight-bold">{{ item.name }}</div>
                  <div class="d-flex flex-wrap ga-1 mt-1">
                    <v-chip v-if="item.job" size="x-small" label color="primary" variant="tonal">
                      {{ item.job }}
                    </v-chip>
                    <v-chip
                        v-for="(interest, idx) in item.interests"
                        :key="idx"
                        size="x-small"
                        label
                        color="secondary"
                        variant="tonal"
                    >
                      {{ interest }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </template>

            <!-- Dynamic schema attribute cells (order follows display_order). -->
            <template
                v-for="attr in schemaAttributes"
                :key="attr.key"
                #[`item.${attr.key}`]="{ item }"
            >
              {{ formatAttributeCell(item, attr.key) }}
            </template>

            <template #item.created_at="{ item }">
              {{ formatDate(item.created_at) }}
            </template>

            <template #item.updated_at="{ item }">
              {{ formatDate(item.updated_at) }}
            </template>

            <template #item.actions="{ item }">
              <div class="d-flex justify-center ga-1">
                <v-tooltip :text="t('generic.edit')" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn
                        v-bind="props"
                        :aria-label="t('generic.edit')"
                        icon="mdi-pencil-outline"
                        variant="text"
                        size="small"
                        color="#0D3B6E"
                        @click="goToEditPersona(item)"
                    ></v-btn>
                  </template>
                </v-tooltip>
                <v-tooltip :text="t('generic.delete')" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn
                        v-bind="props"
                        :aria-label="t('generic.delete')"
                        icon="mdi-delete-outline"
                        variant="text"
                        size="small"
                        color="#D32F2F"
                        @click="openDeleteDialog(item)"
                    ></v-btn>
                  </template>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>
          </v-locale-provider>
        </v-card>
      </v-col>
    </v-row>

    <!-- Add Domain Dialog -->
    <v-dialog v-model="addDomainDialog" max-width="520" persistent>
      <v-card class="add-domain-dialog rounded-xl">
        <v-card-title class="d-flex justify-space-between align-center py-5 px-6">
          <span class="text-h6 font-weight-bold">{{ t('personamanagement.addDomainTitle') }}</span>
          <v-btn
              icon="mdi-close"
              variant="text"
              density="comfortable"
              :aria-label="t('generic.close')"
              @click="closeAddDomainDialog"
          />
        </v-card-title>

        <v-card-text class="px-6 pt-0 pb-2">
          <label class="add-domain-label d-block mb-2">
            {{ t('personamanagement.domainNameLabel') }}
            <span class="text-error">*</span>
          </label>
          <v-text-field
              v-model="newDomainName"
              :placeholder="t('personamanagement.domainNamePlaceholder')"
              variant="outlined"
              autofocus
              hide-details
              density="comfortable"
              class="add-domain-input"
              @keyup.enter="confirmAddDomain"
          />
        </v-card-text>

        <v-card-actions class="px-6 pb-6 pt-4 justify-end ga-3">
          <v-btn variant="outlined" class="add-domain-cancel-btn" @click="closeAddDomainDialog">
            {{ t('generic.cancel') }}
          </v-btn>
          <v-btn color="primary" variant="flat" :loading="creatingDomain" @click="confirmAddDomain">
            {{ t('personamanagement.addDomain') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">{{ t('personamanagement.deletePersona') }}</v-card-title>
        <v-card-text>
          {{ t('personamanagement.deleteConfirmMessage', { name: personaToDelete ? personaToDelete.name : '' }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">{{ t('generic.cancel') }}</v-btn>
          <v-btn color="red" variant="tonal" :loading="deleting" @click="confirmDelete">{{ t('generic.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Feedback snackbars -->
    <v-snackbar v-model="successSnackbar" color="success" location="bottom right" :timeout="3000">
      <v-icon class="mr-2">mdi-check-circle-outline</v-icon>{{ successMessage }}
    </v-snackbar>
    <v-snackbar v-model="errorSnackbar" color="error" location="bottom right" :timeout="4000">
      <v-icon class="mr-2">mdi-alert-circle-outline</v-icon>{{ errorMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import {
  fetch_personas_by_domain,
  fetch_generated_personas,
  fetch_persona_domain_schema,
  delete_persona,
  build_domain_options,
  coerce_allowed_domain,
  get_default_domain,
  get_allowed_domains,
  create_domain,
  refresh_current_user,
} from "@/api.js";
import { useI18n } from "vue-i18n";
import { en as vuetifyEn, ja as vuetifyJa } from 'vuetify/locale';
import { formatDate as formatLocalizedDate } from '@/utils/dateFormat';
import {
  loadStaticPersonasForLookup,
  normalizeAssetReference,
  resolvePersonaImage,
} from '@/utils/personaDomainLoader';
import { canonicalizeSelectValue, expandSelectChoices } from '@/utils/selectChoices';
import { formatAttributeDisplayValue } from '@/utils/attributeValueCoercion';

// Same external placeholder PersonaGrid.vue's "Generated Personas" cards
// already fall back to for a persona with no image of its own — used here
// only for display, never persisted (see getPersonaImage below).
const GENERIC_PERSONA_IMAGE = 'https://randomuser.me/api/portraits/lego/5.jpg';

// Keys that already appear in the identity "Name" column (avatar + display
// name). Still stored on the row for search/edit, but omitted from the
// dynamic attribute columns so first_name/last_name aren't duplicated.
const IDENTITY_ATTRIBUTE_KEYS = new Set(['first_name', 'last_name', 'name', 'surname']);

// Known built-in attribute keys that already have an existing
// personaedit.fields.* translation (confirmed against LABEL_OVERRIDES in
// the backend's persona_domain_schema_defaults.py). Any attribute key not
// in this set is a custom/admin-defined attribute — its column keeps
// showing the actual configured attr.label as-is, never forced through
// translation.
const KNOWN_ATTRIBUTE_LABEL_KEYS = new Set([
  'gender', 'age', 'education_level', 'job_title', 'marital_status',
  'number_of_children', 'country', 'interests',
  'annual_income', 'preferred_contact_method', 'payment_terms', 'area',
  'current_operator', 'plan_type', 'active_services', 'data_usage',
  'average_monthly_spend', 'churn_propensity',
]);

export default {
  name: "PersonaManagement",
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      // Vuetify's own locale message bundles, passed explicitly to the
      // page-scoped v-locale-provider below (the root Vuetify config only
      // registers "en", so "ja" must be supplied here for the data-table's
      // built-in pagination text to localize).
      vuetifyEn,
      vuetifyJa,
      selectedDomain: get_default_domain(),
      personas: [],
      schemaAttributes: [],
      loading: false,
      searchQuery: "",
      deleteDialog: false,
      personaToDelete: null,
      deleting: false,
      genericPersonaImage: GENERIC_PERSONA_IMAGE,
      successSnackbar: false,
      successMessage: "",
      errorSnackbar: false,
      errorMessage: "",
      addDomainDialog: false,
      newDomainName: "",
      creatingDomain: false,
    };
  },
  computed: {
    domainOptions() {
      return build_domain_options(this.$t);
    },
    // Columns come from the domain's persona_domain_schemas metadata,
    // sorted by display_order. Identity Name + timestamps + actions stay fixed.
    headers() {
      const attributeHeaders = this.schemaAttributes
          .filter((attr) => attr.key && !IDENTITY_ATTRIBUTE_KEYS.has(attr.key))
          .map((attr) => ({
            title: attr.label || attr.key,
            key: attr.key,
            sortable: true,
            align: 'start',
          }));

      return [
        { title: this.t('personamanagement.columnName'), key: 'name', sortable: true, align: 'start' },
        ...attributeHeaders,
        { title: this.t('personamanagement.columnCreatedAt'), key: 'created_at', sortable: true, align: 'start' },
        { title: this.t('personamanagement.columnUpdatedAt'), key: 'updated_at', sortable: true, align: 'start' },
        { title: this.t('generic.actions'), key: 'actions', sortable: false, align: 'center' },
      ];
    },
    filteredPersonas() {
      const q = (this.searchQuery || '').trim().toLowerCase();
      if (!q) return this.personas;
      return this.personas.filter((p) => {
        if ((p.name || '').toLowerCase().includes(q)) return true;
        if ((p.job || '').toLowerCase().includes(q)) return true;
        return this.schemaAttributes.some((attr) => {
          const value = p.values?.[attr.key];
          return value != null && String(value).toLowerCase().includes(q);
        });
      });
    },
  },
  async mounted() {
    const fromRoute = (this.$route.query.domain || '').toLowerCase();
    const fromSession = sessionStorage.getItem('generate_selected_domain') || '';
    const preferred = fromRoute || fromSession || null;
    this.selectedDomain = coerce_allowed_domain(preferred);
    if (this.selectedDomain) {
      await this.loadPersonas();
    }
    // Flash a success message after returning from the Edit Persona page,
    // then strip the flag so a refresh doesn't re-trigger it.
    if (this.$route.query.saved === '1') {
      this.showSuccess(this.t('personamanagement.editSuccess'));
      this.$router.replace({ name: 'persona-management', query: { domain: this.selectedDomain } });
    }
  },
  methods: {
    onDomainChange() {
      const q = { ...(this.$route.query || {}), domain: this.selectedDomain };
      this.$router.replace({ name: 'persona-management', query: q });
      this.loadPersonas();
    },

    // Optional static catalog for image / country enrichment only (B5).
    async loadStaticPersonas(domain) {
      return loadStaticPersonasForLookup(domain);
    },

    async loadSchema(domain) {
      try {
        const response = await fetch_persona_domain_schema(domain);
        const attributes = response.data?.attributes || [];
        this.schemaAttributes = [...attributes].sort(
            (a, b) => (a.display_order || 0) - (b.display_order || 0)
        );
      } catch (error) {
        console.error('Failed to load persona domain schema:', error);
        this.schemaAttributes = [];
      }
    },

    // Resolve a schema attribute value from the merged persona shape:
    // top-level data, nested personal_characteristics, and known aliases.
    resolveAttributeValue(merged, data, key) {
      const pc = merged.personal_characteristics || {};
      // Keep bidirectional with PersonaInfo / PersonaEditPersona so CRM schema
      // keys (telco) resolve to narrative persona JSON keys and vice versa.
      const aliases = {
        occupation: ['occupation', 'job_title'],
        job_title: ['job_title', 'occupation'],
        first_name: ['first_name', 'name'],
        last_name: ['last_name', 'surname'],
        civil_status: ['civil_status', 'civilStatus', 'marital_status'],
        marital_status: ['marital_status', 'civil_status', 'civilStatus'],
        geographic_area_of_residence: ['geographic_area_of_residence', 'residence', 'area'],
        residence: ['residence', 'geographic_area_of_residence', 'area'],
        area: ['area', 'geographic_area_of_residence', 'residence'],
        qualification: ['qualification', 'education_level'],
        education_level: ['education_level', 'qualification'],
        income: ['income', 'annual_income'],
        annual_income: ['annual_income', 'income'],
        personal_interests: ['personal_interests', 'personalInterests', 'interests'],
        segments: ['segments', 'segmentation', 'segment'],
        segmentation: ['segmentation', 'segments', 'segment'],
      };
      const candidates = aliases[key] || [key];

      for (const candidate of candidates) {
        if (data[candidate] !== undefined && data[candidate] !== null && data[candidate] !== '') {
          return data[candidate];
        }
        if (pc[candidate] !== undefined && pc[candidate] !== null && pc[candidate] !== '') {
          return pc[candidate];
        }
        if (merged[candidate] !== undefined && merged[candidate] !== null && merged[candidate] !== '') {
          return merged[candidate];
        }
      }
      return null;
    },

    formatAttributeCell(item, key) {
      const raw = item.values?.[key];
      if (raw === null || raw === undefined || raw === '') return '—';
      const attr = (this.schemaAttributes || []).find((a) => a.key === key);
      const value = formatAttributeDisplayValue(raw, attr);
      if (value === null || value === undefined || value === '') return '—';
      if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
      const unit = typeof attr?.options?.unit === 'string' ? attr.options.unit.trim() : '';
      return unit ? `${value} ${unit}` : value;
    },

    // Overlay a backend document's tracked fields onto the matching static
    // persona — same reconciliation PersonaGrid.vue uses, so this page shows
    // the same semantic info (country, image, interests baseline) as the
    // normal Persona page. Country never falls back to
    // geographic_area_of_residence/residence — those are regions, not
    // countries.
    overlayBackendData(staticPersona, backendDoc) {
      const data = backendDoc.data || {};
      const beCharKey = data.personal_characteristics
          ? 'personal_characteristics'
          : (data.personalCharacteristics ? 'personalCharacteristics' : null);
      const beChars = beCharKey ? data[beCharKey] : null;
      const staticChars = staticPersona.personal_characteristics || staticPersona.personalCharacteristics || {};

      const merged = { ...staticPersona };
      merged.personal_characteristics = { ...staticChars, ...(beChars || {}) };
      // Freshly generated personas store age/job_title as flat top-level
      // fields rather than nested under personal_characteristics — fall
      // back to them only when the preferred nested/edited value is absent.
      if (merged.personal_characteristics.age === undefined && data.age !== undefined) {
        merged.personal_characteristics.age = data.age;
      }
      if (!merged.personal_characteristics.occupation && data.job_title) {
        merged.personal_characteristics.occupation = data.job_title;
      }

      const beInterests = data.personal_interests || data.personalInterests || data['Personal Interests'];
      merged.personal_interests = beInterests || staticPersona.personal_interests || staticPersona.personalInterests || [];

      merged.country = data.country || staticPersona.country || '';

      // Name is an editable display field (persona_id is the stable
      // identity) — always show the backend's current name, not the
      // static file's original one. This matters once a rename means the
      // two no longer match by name and the image-anchor fallback in
      // buildRow was used to find this static entry.
      merged.title = backendDoc.name || staticPersona.title || staticPersona.name;

      // The backend's own saved image (set via "Change Image") is
      // authoritative once present — this matters for a persona with no
      // static counterpart at all (staticPersona is just a {title} stub
      // with no .image/.name), where getPersonaImage would otherwise fall
      // through to the generic placeholder despite an image already
      // having been explicitly chosen and saved for this persona.
      if (data.image) {
        merged.image = data.image;
      }

      return merged;
    },

    // Resolves a persona's actual image identity — a real static-derived
    // path, or an explicitly-saved data.image. Returns '' when neither
    // applies (no static association, nothing explicitly chosen) rather
    // than fabricating a catalog path — callers apply a display-only
    // placeholder (genericPersonaImage) where this is used for rendering,
    // so nothing invented here ever gets persisted back to the persona.
    getPersonaImage(persona) {
      return resolvePersonaImage(persona, { fallback: 'empty' });
    },

    // Every row here is a backend document (persona_id required for edit/delete).
    // The primary match key is the current display name; if the persona was
    // renamed via this same edit flow, that name no longer appears in the
    // static file, so fall back to the image path captured at the time of
    // the first edit (see saveEdit below). The fallback only resolves when
    // exactly one static persona has that image AND that static persona
    // isn't already correctly matched to a different backend document by
    // name (claimedNames) — otherwise an admin picking an image that
    // happens to already belong to some other, unrelated static persona
    // (e.g. via "Change Image") would incorrectly borrow that persona's
    // static-only fields (country, etc.). Zero or multiple real candidates
    // is treated as unresolved rather than guessing.
    buildRow(doc, staticByName, staticList, claimedNames) {
      const key = (doc.name || '').trim().toLowerCase();
      let staticMatch = staticByName.get(key);
      if (!staticMatch && doc.data && doc.data.image) {
        const candidates = staticList.filter((p) =>
            normalizeAssetReference(this.getPersonaImage(p)) === normalizeAssetReference(doc.data.image)
            && !claimedNames.has((p.title || p.name || '').trim().toLowerCase())
        );
        if (candidates.length === 1) staticMatch = candidates[0];
      }
      staticMatch = staticMatch || { title: doc.name };
      const merged = this.overlayBackendData(staticMatch, doc);
      const data = doc.data || {};
      const pc = merged.personal_characteristics || {};

      const values = {};
      for (const attr of this.schemaAttributes) {
        let value = this.resolveAttributeValue(merged, data, attr.key);
        value = formatAttributeDisplayValue(value, attr);
        if (attr.type === 'single_select' && value !== null && value !== undefined && value !== '') {
          value = canonicalizeSelectValue(
            value,
            expandSelectChoices(attr.options?.choices),
          );
        }
        values[attr.key] = value;
      }

      const reservedKeys = new Set([
        'persona_id', 'domain', 'name', 'age', 'job', 'country', 'interests',
        'image', 'created_at', 'updated_at', 'values', 'raw',
      ]);
      const flatValues = Object.fromEntries(
          Object.entries(values).filter(([k]) => !reservedKeys.has(k))
      );

      return {
        persona_id: doc.persona_id,
        domain: doc.domain,
        name: merged.title || merged.name || doc.name || '',
        // Keep legacy convenience fields used by chips / older callers.
        age: values.age ?? (pc.age !== undefined && pc.age !== null ? pc.age : null),
        job: values.occupation || values.job_title || pc.occupation || '',
        country: values.country || merged.country || '',
        interests: merged.personal_interests || [],
        image: this.getPersonaImage(merged),
        created_at: doc.created_at || null,
        updated_at: doc.updated_at || null,
        values,
        // Flatten attribute values onto the row so v-data-table can sort by key.
        ...flatValues,
        raw: doc,
      };
    },

    async loadPersonas() {
      this.loading = true;
      try {
        // Schema must be ready before buildRow so attribute columns/values
        // follow the saved display_order for this domain.
        await this.loadSchema(this.selectedDomain);

        const [manualRes, generatedRes, staticList] = await Promise.all([
          fetch_personas_by_domain(this.selectedDomain),
          fetch_generated_personas(this.selectedDomain),
          this.loadStaticPersonas(this.selectedDomain).catch(() => []),
        ]);
        const backendDocs = [...(manualRes.data || []), ...(generatedRes.data || [])];
        const staticByName = new Map(
            staticList.map((p) => [(p.title || p.name || '').trim().toLowerCase(), p])
        );
        // Static personas already correctly resolved by a primary name
        // match — not eligible as an image-fallback candidate for a
        // different, unrelated backend document (see buildRow).
        const claimedNames = new Set(
            backendDocs
                .map((d) => (d.name || '').trim().toLowerCase())
                .filter((n) => staticByName.has(n))
        );

        this.personas = backendDocs.map((doc) => this.buildRow(doc, staticByName, staticList, claimedNames));
      } catch (error) {
        console.error('Failed to load personas:', error);
        this.personas = [];
        this.schemaAttributes = [];
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateStr) {
      return formatLocalizedDate(dateStr, this.$i18n.locale);
    },
    showSuccess(message) {
      this.successMessage = message;
      this.successSnackbar = true;
    },
    showError(error, fallback) {
      this.errorMessage = error?.response?.data?.detail || fallback;
      this.errorSnackbar = true;
    },

    // ── Edit ───────────────────────────────────────────
    // Hand the already-resolved row (name/image/domain/raw backend doc) to
    // the dedicated Edit Persona page via sessionStorage, mirroring the
    // existing PersonaGrid.vue → PersonaInfo.vue handoff pattern, so the
    // edit page doesn't need to redo the static/backend match on the happy
    // path. It re-resolves from persona_id on a direct load/refresh.
    goToEditPersona(row) {
      sessionStorage.setItem('editPersonaRow', JSON.stringify(row));
      this.$router.push({
        name: 'persona-management-edit',
        params: { personaId: row.persona_id },
        query: { domain: this.selectedDomain },
      });
    },

    goToFrameworkEditor() {
      this.$router.push({
        name: 'persona-framework-editor',
        params: { domainId: this.selectedDomain },
      });
    },

    goToCreatePersona() {
      this.$router.push({
        name: 'generate-personas',
        query: { domain: this.selectedDomain },
      });
    },

    closeAddDomainDialog() {
      this.addDomainDialog = false;
      this.newDomainName = '';
    },

    async confirmAddDomain() {
      const name = (this.newDomainName || '').trim();
      if (!name) {
        this.showError(null, this.t('personamanagement.domainNameRequired'));
        return;
      }

      this.creatingDomain = true;
      try {
        const response = await create_domain(name, null);
        await refresh_current_user();
        const domainId = response.data.value;
        this.closeAddDomainDialog();
        this.$router.push({
          name: 'persona-framework-editor',
          params: { domainId },
        });
      } catch (error) {
        const detail = error.response?.data?.detail;
        this.showError(error, typeof detail === 'string' ? detail : this.t('personamanagement.addDomainError'));
      } finally {
        this.creatingDomain = false;
      }
    },

    // ── Delete ─────────────────────────────────────────
    openDeleteDialog(row) {
      this.personaToDelete = row;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      this.deleting = true;
      try {
        await delete_persona(this.personaToDelete.persona_id);
        this.personas = this.personas.filter((p) => p.persona_id !== this.personaToDelete.persona_id);
        this.showSuccess(this.t('personamanagement.deleteSuccess'));
      } catch (error) {
        console.error('Failed to delete persona:', error);
        this.showError(error, this.t('personamanagement.deleteError'));
      } finally {
        this.deleting = false;
        this.deleteDialog = false;
        this.personaToDelete = null;
      }
    },
  },
};
</script>

<style scoped>
.controls-left {
  flex: 1 1 520px;
  min-width: 0;
}

.domain-select {
  width: 280px;
  max-width: 280px;
  flex: 0 0 280px;
}

.search-field {
  flex: 1 1 320px;
  min-width: 240px;
  max-width: 560px;
}

.add-domain-dialog {
  overflow: hidden;
}

.add-domain-label {
  color: #1a1a2e;
  font-size: 0.95rem;
  font-weight: 500;
}

.add-domain-input :deep(.v-field) {
  border-radius: 8px;
}

.add-domain-cancel-btn {
  border-color: #d0d0d0;
  color: #1a1a2e;
  min-width: 96px;
}

.persona-management-card {
  border-color: #9E9E9E;
  background-color: white;
}

.personas-table {
  border: 1px solid #E0E0E0;
  border-radius: 8px;
}

.table-header-row th {
  background-color: #F5F5F5 !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
  text-align: left !important;
}

.personas-table :deep(tbody td) {
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

.persona-name-cell {
  min-width: 260px;
}

.persona-avatar {
  border: 2px solid #e0e0e0;
}
</style>
